const NODE_POSITIONS = {
    client: { x: 500, y: 330 },
    server1: { x: 500, y: 102 },
    server2: { x: 815, y: 248 },
    server3: { x: 695, y: 520 },
    server4: { x: 305, y: 520 },
    server5: { x: 185, y: 248 },
    server6: { x: 500, y: 632 },
};

function MessagePulse({ from, to, dropped }) {
    const start = NODE_POSITIONS[from];
    const end = NODE_POSITIONS[to];
    if (!start || !end) return null;

    if (from === to) {
        return (
            <circle className="timer-pulse" cx={start.x} cy={start.y} r="62" aria-hidden="true">
                <animate attributeName="r" values="58;88" dur="1.35s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" dur="1.35s" repeatCount="indefinite" />
            </circle>
        );
    }

    const destination = dropped
        ? { x: start.x + ((end.x - start.x) * 0.68), y: start.y + ((end.y - start.y) * 0.68) }
        : end;

    return (
        <g
            className={`paxos-message${dropped ? ' is-dropped' : ''}`}
            data-from={from}
            data-to={to}
            mask="url(#paxos-client-occlusion)"
            aria-hidden="true"
        >
            <line x1={start.x} y1={start.y} x2={destination.x} y2={destination.y} markerEnd={dropped ? undefined : 'url(#message-arrow)'} />
            <circle className="paxos-message-packet" cx={start.x} cy={start.y} r="5">
                <animate attributeName="cx" from={start.x} to={destination.x} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                <animate attributeName="cy" from={start.y} to={destination.y} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                {dropped && <animate attributeName="opacity" values="1;1;0" keyTimes="0;0.65;1" begin="0s" dur="1.25s" repeatCount="indefinite" />}
            </circle>
            {dropped && (
                <g className="drop-marker" transform={`translate(${destination.x} ${destination.y})`}>
                    <circle r="12" />
                    <path d="M -4 -4 L 4 4 M 4 -4 L -4 4" />
                </g>
            )}
        </g>
    );
}

function NetworkNode({ node, selected, sender, recipient, droppedRecipient, changed, changedSlots, onSelect }) {
    const { x, y } = NODE_POSITIONS[node.id];
    const chosenCount = node.log.filter(({ status }) => status === 'CHOSEN').length;

    return (
        <g
            className={`paxos-node is-${node.role} is-${node.health}${selected ? ' is-selected' : ''}${sender ? ' is-sender' : ''}${recipient ? ' is-recipient' : ''}${droppedRecipient ? ' is-dropped-recipient' : ''}${changed ? ' has-change' : ''}`}
            transform={`translate(${x - 80} ${y - 54})`}
            onClick={() => onSelect(node.id)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelect(node.id);
                }
            }}
            role="button"
            tabIndex="0"
            aria-label={`${node.label}, ${node.role}, ${node.health}`}
        >
            <rect width="160" height="108" rx="10" />
            <circle className="node-health" cx="18" cy="19" r="4" />
            <text className="node-name" x="30" y="23">{node.label}</text>
            <text className="node-role" x="143" y="23" textAnchor="end">{node.role}</text>
            <text className="node-meta" x="18" y="52">ballot {node.ballot}</text>
            <text className="node-meta" x="18" y="72">executed {node.executed}</text>
            <g transform="translate(18 85)">
                {node.log.map((entry, index) => (
                    <rect
                        key={`${node.id}-${index}`}
                        className={`mini-slot is-${entry.status.toLowerCase()}${changedSlots.includes(index) ? ' has-change' : ''}`}
                        x={index * 25}
                        width="19"
                        height="9"
                        rx="2"
                    />
                ))}
                <text className="node-log-label" x="80" y="9">{chosenCount}/3 chosen</text>
            </g>
        </g>
    );
}

export default function PaxosNetwork({ frame, previousFrame, selectedNode, onSelectNode }) {
    const targets = Array.isArray(frame.to) ? frame.to : [];
    const memberNodeIds = frame.nodes.filter(({ role }) => role !== 'not-member').map(({ id }) => id);
    const hub = frame.nodes.find(({ role }) => role === 'leader')?.id
        || frame.nodes.find(({ role }) => role === 'candidate')?.id
        || memberNodeIds[0];
    const serverLinks = memberNodeIds.filter((id) => id !== hub).map((id) => [hub, id]);
    const previousNodes = previousFrame?.nodes || [];

    const nodeChanged = (node) => {
        const previous = previousNodes.find(({ id }) => id === node.id);
        return !previous || JSON.stringify(previous) !== JSON.stringify(node);
    };

    const changedSlots = (node) => {
        const previous = previousNodes.find(({ id }) => id === node.id);
        if (!previous) return node.log.map((_, index) => index);
        return node.log.reduce((changedIndexes, entry, index) => {
            if (JSON.stringify(entry) !== JSON.stringify(previous.log[index])) changedIndexes.push(index);
            return changedIndexes;
        }, []);
    };

    return (
        <svg className="paxos-network" viewBox="0 0 1000 700" role="img" aria-label={`${frame.nodes.length}-server Paxos cluster and client`}>
            <defs>
                <pattern id="paxos-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                    <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
                <marker id="message-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" className="message-arrow-head" />
                </marker>
                <mask id="paxos-client-occlusion" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="700">
                    <rect width="1000" height="700" fill="white" />
                    <rect x="440" y="301" width="120" height="58" rx="29" fill="black" />
                </mask>
            </defs>
            <rect className="network-grid" width="1000" height="700" fill="url(#paxos-grid)" />
            <ellipse className="membership-ring" cx="500" cy="326" rx="355" ry="260" />
            <text className="membership-label" x="92" y="618">Configured membership · 5 voters</text>
            {serverLinks.map(([from, to]) => (
                <line
                    className="network-link"
                    key={`${from}-${to}`}
                    x1={NODE_POSITIONS[from].x}
                    y1={NODE_POSITIONS[from].y}
                    x2={NODE_POSITIONS[to].x}
                    y2={NODE_POSITIONS[to].y}
                />
            ))}
            {memberNodeIds.map((server) => (
                <line
                    className="network-link is-client-link"
                    key={`client-${server}`}
                    x1={NODE_POSITIONS.client.x}
                    y1={NODE_POSITIONS.client.y}
                    x2={NODE_POSITIONS[server].x}
                    y2={NODE_POSITIONS[server].y}
                />
            ))}

            {targets.map((target) => (
                <MessagePulse
                    key={`${frame.id}-${target}`}
                    from={frame.from}
                    to={target}
                    dropped={frame.dropped || frame.droppedTargets?.includes(target)}
                />
            ))}

            <g className="client-node" transform="translate(440 301)">
                <rect width="120" height="58" rx="29" />
                <circle cx="22" cy="29" r="5" />
                <text x="38" y="27">PaxosClient</text>
                <text className="client-sequence" x="38" y="42">sequence #1</text>
            </g>

            {frame.nodes.map((node) => (
                <NetworkNode
                    key={node.id}
                    node={node}
                    selected={selectedNode === node.id}
                    sender={frame.from === node.id}
                    recipient={targets.includes(node.id) && !frame.droppedTargets?.includes(node.id) && !frame.dropped}
                    droppedRecipient={targets.includes(node.id) && (frame.droppedTargets?.includes(node.id) || frame.dropped)}
                    changed={nodeChanged(node)}
                    changedSlots={changedSlots(node)}
                    onSelect={onSelectNode}
                />
            ))}
        </svg>
    );
}
